import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'
import PublicLayout from '../layouts/PublicLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'

const routes: Array<RouteRecordRaw> = [
  // Public routes (no authentication required)
  {
    path: '/',
    component: PublicLayout,
    children: [
      {
        name: 'home',
        path: '',
        component: () => import('../pages/public/Home.vue'),
      },
      {
        name: 'public-pricing',
        path: 'pricing',
        component: () => import('../pages/pricing-plans/PricingPlans.vue'),
      },
      {
        name: 'docs',
        path: 'docs',
        component: () => import('../pages/public/Documentation.vue'),
      },
      {
        name: 'services',
        path: 'services',
        component: () => import('../pages/services/Services.vue'),
      },
    ],
  },
  // Admin routes (authentication required)
  {
    name: 'admin',
    path: '/app',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    meta: { requiresAuth: true },
    children: [
      {
        name: 'dashboard',
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'users',
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'projects',
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'api-tokens',
        path: 'api-tokens',
        component: () => import('../pages/api-tokens/ApiTokensPage.vue'),
        meta: { requiresAuth: true },
      },
      {
        name: 'payments',
        path: 'payments',
        component: RouteViewComponent,
        meta: { requiresAuth: true },
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
            meta: { requiresAuth: true },
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
            meta: { requiresAuth: true },
          },
        ],
      },
      {
        name: 'faq',
        path: 'faq',
        component: () => import('../pages/faq/FaqPage.vue'),
        meta: { requiresAuth: true },
      },
    ],
  },
  // Auth routes
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        name: 'reset-password',
        path: 'reset-password',
        component: () => import('../pages/auth/ResetPassword.vue'),
      },
      {
        name: 'confirm-email',
        path: 'confirm-email',
        component: () => import('../pages/auth/ConfirmEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  // 404 and catch-all
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: '404' },
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const { useAuthStore } = await import('../stores/auth-store')
  const authStore = useAuthStore()

  // Wait for auth initialization
  if (!authStore.isInitialized) {
    await authStore.initAuth()
  }

  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth)
  const isAuthPage = to.path.startsWith('/auth')
  const isTokenConsumptionPage = ['confirm-email', 'reset-password'].includes(String(to.name ?? ''))

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login if trying to access protected route, save intended destination
    next({ name: 'login', query: { redirect: to.fullPath } })
  } else if (isAuthPage && authStore.isAuthenticated && !isTokenConsumptionPage) {
    // Redirect to dashboard if already logged in and trying to access auth pages
    next({ name: 'dashboard' })
  } else {
    // Public page or authorized access
    next()
  }
})

export default router
